import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiRequestError } from "../../../api";
import { aiApi, aiKeys } from "../api";
import { useAiCapability } from "../hooks";
import { Skeleton } from "../../../components/States";
import { AiBlockedTooltip, AiOutput, AiSparkle, ArtifactMetaLine, CitationChips, type CitationTools } from "./common";

export function outcomeBadge(outcome: "completed" | "abandoned" | "unknown"): { cls: string; label: string } {
  return outcome === "completed" ? { cls: "ok", label: "completed" } : outcome === "abandoned" ? { cls: "danger", label: "abandoned" } : { cls: "neutral", label: "outcome unknown" };
}

const POLL_MS = 3000;
// How long a pending summary can sit before the card stops calling it progress
// and starts calling it a stalled queue. Generating one takes seconds; minutes
// means nothing is draining the queue.
const STALL_MS = 2 * 60_000;

/**
 * Session summary card (automatic AI, never metered).
 *
 * Whether a summary is being written is read from the server, not remembered
 * here: the work runs on a worker and outlives the page, so a flag in component
 * state would be lost on the next refresh and could never report a failure.
 */
export function SummaryCard({ sessionId, tools, compact }: { sessionId: string; tools?: CitationTools; compact?: boolean }) {
  const ai = useAiCapability();
  // Summaries are produced in the background, so there is nothing to wait for
  // when the deployment has no provider: say so instead of polling for an
  // artifact that will never be written.
  const q = useQuery({
    queryKey: aiKeys.summary(sessionId),
    queryFn: () => aiApi.summary(sessionId),
    enabled: ai.settled && !ai.blocked,
    retry: false,
    staleTime: 60_000,
    // Polling follows the server's own answer, so it starts and stops with the
    // actual work rather than with a guess made when the button was clicked.
    refetchInterval: (query) => (query.state.data?.status.state === "pending" ? POLL_MS : false),
  });

  const status = q.data?.status;
  const pending = status?.state === "pending";
  const stalled = pending && !!status?.requestedAt && Date.now() - Date.parse(status.requestedAt) > STALL_MS;

  const qc = useQueryClient();
  const requeue = useMutation({
    mutationFn: () => aiApi.resummarize(sessionId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: aiKeys.summary(sessionId) }),
  });
  // A stalled queue is not progress. Calling it progress disables the one control
  // that could clear it, and contradicts the note right below saying nothing has
  // picked the work up.
  const busy = requeue.isPending || (pending && !stalled);

  const action = (label: string) => (
    <AiBlockedTooltip ai={ai}>
      <button
        type="button"
        className="btn ghost sm ai-btn"
        onClick={() => requeue.mutate()}
        disabled={ai.blocked || busy}
        title={ai.titleFor(`${label} this session with the current AI provider`)}
        data-testid="resummarize"
      >
        <AiSparkle size={12} />
        {busy ? "Summarizing…" : label}
      </button>
    </AiBlockedTooltip>
  );

  // The reason a summary is absent or out of date, when there is one worth saying.
  const note = () => {
    if (requeue.isError) return <Note testId="summary-note">Could not queue a new summary: {(requeue.error as ApiRequestError).message}</Note>;
    if (stalled) return <Note testId="summary-note">Still waiting. Nothing has picked this up, so the background worker may not be running.</Note>;
    if (pending) return <Note testId="summary-note">Writing a summary…</Note>;
    if (status?.state === "failed") return <Note testId="summary-note">{status.error || "The last attempt to summarize this session failed."}</Note>;
    return null;
  };

  if (ai.blocked) {
    return (
      <div className="card ai-card" data-testid="summary-card">
        <h3>Summary <span className="badge ai">AI</span></h3>
        <p className="muted small" style={{ margin: 0 }} data-testid="summary-unavailable" title={ai.hint}>
          {ai.hint}.
        </p>
      </div>
    );
  }
  if (q.isPending) return <div className="card ai-card" data-testid="summary-card"><Skeleton lines={3} /></div>;
  if (q.isError) {
    return (
      <div className="card ai-card" data-testid="summary-card">
        <h3>Summary <span className="badge ai">AI</span></h3>
        <p className="muted small" style={{ margin: 0 }}>Summary unavailable: {(q.error as ApiRequestError).message}</p>
      </div>
    );
  }
  const s = q.data.summary;
  if (!s) {
    return (
      <div className="card ai-card" data-testid="summary-card">
        <h3>Summary <span className="badge ai">AI</span></h3>
        {!pending && status?.state !== "failed" && (
          <p className="muted small" style={{ margin: 0 }} data-testid="summary-empty">
            Not summarized yet.
          </p>
        )}
        {note()}
        <div className="ai-meta">{action("Summarize")}</div>
      </div>
    );
  }
  const o = outcomeBadge(s.outcome);
  return (
    <AiOutput className="card ai-card" testId="summary-card">
      <h3>
        Summary <span className={`badge ${o.cls}`}>{o.label}</span>
      </h3>
      {/* Named so a host with a fixed height can scroll the prose and leave the
          heading, friction bar and actions in place. */}
      <p className="summary-narrative scroll-cue">{s.narrative}</p>
      <div className="friction" aria-label={`Friction score ${s.frictionScore} of 100`}>
        <span className="muted">Friction</span>
        <div className="bar"><span style={{ width: `${Math.max(0, Math.min(100, s.frictionScore))}%` }} /></div>
        <span className="mono">{s.frictionScore}</span>
      </div>
      {!compact && s.highlights.length > 0 && (
        <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
          {s.highlights.map((h, i) => (
            <li key={i}>
              {h.text}
              <CitationChips citations={h.citations} tools={tools} />
            </li>
          ))}
        </ul>
      )}
      <ArtifactMetaLine meta={s.meta}>{action("Re-summarize")}</ArtifactMetaLine>
      {note()}
    </AiOutput>
  );
}

function Note({ children, testId }: { children: React.ReactNode; testId: string }) {
  return (
    <p className="muted small" role="status" style={{ margin: "4px 0 0" }} data-testid={testId}>
      {children}
    </p>
  );
}
